import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../components/Loading';
import InputCpfCnpj from '../../../components/MaskedInput/InputCpfCnpj';
import { sanitizeDoc, validateCpfCnpj } from '../../../utils/validateCpfCnpj';
import {
   getBankAccount,
   createBankAccount,
   updateBankAccount,
} from '../../../services/finance/bankAccountsApi';
import { readApiError, financeErrorMessage } from '../utils/apiError';
import { ACCOUNT_TYPE_OPTIONS, PIX_KEY_TYPE_OPTIONS } from '../constants';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../styles/forms/Field';
import { PrimaryButton, GhostButton } from '../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Financeiro · Contas bancárias — cadastro e edição (handoff 0079 §3.1).
//
// Uma conta = um CNPJ recebedor. A CHAVE DA ASAAS NÃO ENTRA AQUI: credencial é
// outro recurso, gerenciada na ficha da conta (§3.2). Aqui também não há campo
// de situação — ativar/desativar tem endpoint próprio e vive na ficha.
//
// O documento pode ir formatado: o backend valida o dígito verificador e
// normaliza para só dígitos. A validação no cliente (mesmo algoritmo) evita a
// ida ao servidor no erro de digitação mais comum.
// ---------------------------------------------------------------------------

const FIELD_ERROR_STYLE = {
   fontSize: 11,
   color: '#b71c1c',
   marginTop: 2,
};

// Campos opcionais: no cadastro, os vazios são OMITIDOS do corpo; na edição,
// um campo que TINHA valor e foi esvaziado vai como null (intenção explícita de
// limpar), enquanto os que já estavam vazios seguem omitidos.
const OPTIONAL_FIELDS = [
   'bank_name',
   'bank_code',
   'agency',
   'agency_digit',
   'account_number',
   'account_digit',
   'account_type',
   'pix_key',
   'pix_key_type',
];

const EMPTY_FORM = {
   account_name: '',
   account_owner_name: '',
   account_doc_number: '',
   bank_name: '',
   bank_code: '',
   agency: '',
   agency_digit: '',
   account_number: '',
   account_digit: '',
   account_type: '',
   pix_key: '',
   pix_key_type: '',
};

function accountToForm(account) {
   const next = { ...EMPTY_FORM };
   Object.keys(EMPTY_FORM).forEach((key) => {
      next[key] = account?.[key] ?? '';
   });
   return next;
}

export default function BankAccountForm() {
   const navigate = useNavigate();
   const { id } = useParams();
   const isEdit = Boolean(id);

   const [form, setForm] = useState(EMPTY_FORM);
   const [initialForm, setInitialForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isLoading, setIsLoading] = useState(isEdit);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const fetchAccount = useCallback(async () => {
      setIsLoading(true);
      try {
         const account = await getBankAccount(id);
         if (!account) {
            toast.error('Conta bancária não encontrada.');
            navigate('/finance/bank-accounts', { replace: true });
            return;
         }
         const loaded = accountToForm(account);
         setForm(loaded);
         setInitialForm(loaded);
      } catch (err) {
         toast.error(financeErrorMessage(err, 'Erro ao carregar a conta.'));
         navigate('/finance/bank-accounts', { replace: true });
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      if (isEdit) fetchAccount();
   }, [isEdit, fetchAccount]);

   const updateField = (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
         if (!prev[key]) return prev;
         const next = { ...prev };
         delete next[key];
         return next;
      });
   };

   const buildPayload = () => {
      const payload = {
         account_name: form.account_name.trim(),
         account_owner_name: form.account_owner_name.trim(),
         // Vai formatado; o backend normaliza para só dígitos.
         account_doc_number: form.account_doc_number.trim(),
      };

      OPTIONAL_FIELDS.forEach((key) => {
         const value = String(form[key] ?? '').trim();
         if (value) {
            payload[key] = value;
            return;
         }
         // Esvaziou algo que estava preenchido → limpar de fato.
         if (isEdit && String(initialForm[key] ?? '').trim()) {
            payload[key] = null;
         }
      });

      return payload;
   };

   const validateLocally = () => {
      const errors = {};

      if (!form.account_name.trim()) {
         errors.account_name = 'Informe o apelido da conta.';
      }
      if (!form.account_owner_name.trim()) {
         errors.account_owner_name = 'Informe o titular da conta.';
      }

      const docDigits = sanitizeDoc(form.account_doc_number);
      if (!docDigits) {
         errors.account_doc_number = 'Informe o CPF ou CNPJ do titular.';
      } else {
         const { valid, reason } = validateCpfCnpj(docDigits);
         if (!valid) errors.account_doc_number = reason ?? 'Documento inválido.';
      }

      // Chave PIX sem tipo (ou vice-versa) não é erro do backend, mas quase
      // sempre é descuido: avisa sem bloquear o cadastro dos demais campos.
      if (form.pix_key.trim() && !form.pix_key_type) {
         errors.pix_key_type = 'Informe o tipo da chave PIX.';
      }

      setFieldErrors(errors);
      return Object.keys(errors).length === 0;
   };

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (isSubmitting) return;
      if (!validateLocally()) return;

      setIsSubmitting(true);
      try {
         if (isEdit) {
            const { message } = await updateBankAccount(id, buildPayload());
            toast.success(message);
            navigate(`/finance/bank-accounts/${id}`, { replace: true });
         } else {
            const { bankAccount, message } = await createBankAccount(
               buildPayload(),
            );
            toast.success(message);
            // Manda direto para a ficha: é lá que a credencial é cadastrada, e
            // sem credencial a conta ainda não emite nada.
            if (bankAccount?.id) {
               navigate(`/finance/bank-accounts/${bankAccount.id}`, {
                  replace: true,
               });
            } else {
               navigate('/finance/bank-accounts', { replace: true });
            }
         }
      } catch (err) {
         const { fieldErrors: apiFieldErrors, code } = readApiError(err);
         if (Object.keys(apiFieldErrors).length) {
            // 422 com campo → inline, nunca toast (FC4 B.3).
            setFieldErrors(apiFieldErrors);
         } else if (code === 'unique') {
            setFieldErrors({
               account_name: 'Já existe uma conta com este apelido.',
            });
         } else {
            toast.error(financeErrorMessage(err, 'Erro ao salvar a conta.'));
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleCancel = () => {
      if (isEdit) navigate(`/finance/bank-accounts/${id}`);
      else navigate('/finance/bank-accounts');
   };

   const renderError = (key) =>
      fieldErrors[key] ? (
         <span style={FIELD_ERROR_STYLE}>{fieldErrors[key]}</span>
      ) : null;

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {isEdit ? 'Editar conta bancária' : 'Nova conta bancária'}
               </PageTitle>
               <PageSubtitle>
                  {isEdit
                     ? 'Dados cadastrais da conta recebedora'
                     : 'Cadastre a conta recebedora; a credencial da ASAAS é o passo seguinte, na ficha da conta'}
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            {/* ─── Identificação ──────────────────────────────────────────── */}
            <Section>
               <SectionHeader>
                  <SectionTitle>Identificação</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="account-name">
                           Apelido da conta
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="account-name"
                           type="text"
                           maxLength={120}
                           placeholder="Ex: MYSHOP GO"
                           value={form.account_name}
                           onChange={(e) =>
                              updateField('account_name', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.account_name || undefined}
                        />
                        {renderError('account_name')}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="account-owner">
                           Titular
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="account-owner"
                           type="text"
                           maxLength={255}
                           placeholder="Razão social ou nome do titular"
                           value={form.account_owner_name}
                           onChange={(e) =>
                              updateField('account_owner_name', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={
                              !!fieldErrors.account_owner_name || undefined
                           }
                        />
                        {renderError('account_owner_name')}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="account-doc">
                           CPF / CNPJ do titular
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <InputCpfCnpj
                           name="account-doc"
                           value={form.account_doc_number}
                           onChange={(e) =>
                              updateField('account_doc_number', e.target.value)
                           }
                           disabled={isSubmitting}
                           required
                        />
                        {renderError('account_doc_number')}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            {/* ─── Dados bancários ────────────────────────────────────────── */}
            <Section>
               <SectionHeader>
                  <SectionTitle>Dados bancários</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="bank-name">Banco</label>
                        <input
                           id="bank-name"
                           type="text"
                           maxLength={120}
                           placeholder="Ex: Banco do Brasil"
                           value={form.bank_name}
                           onChange={(e) =>
                              updateField('bank_name', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('bank_name')}
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="bank-code">Código</label>
                        <input
                           id="bank-code"
                           type="text"
                           maxLength={10}
                           placeholder="001"
                           value={form.bank_code}
                           onChange={(e) =>
                              updateField('bank_code', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('bank_code')}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="account-type">Tipo de conta</label>
                        <select
                           id="account-type"
                           value={form.account_type}
                           onChange={(e) =>
                              updateField('account_type', e.target.value)
                           }
                           disabled={isSubmitting}
                        >
                           {ACCOUNT_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                        {renderError('account_type')}
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="agency">Agência</label>
                        <input
                           id="agency"
                           type="text"
                           maxLength={20}
                           placeholder="1234"
                           value={form.agency}
                           onChange={(e) =>
                              updateField('agency', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('agency')}
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="agency-digit">Dígito da agência</label>
                        <input
                           id="agency-digit"
                           type="text"
                           maxLength={4}
                           placeholder="X"
                           value={form.agency_digit}
                           onChange={(e) =>
                              updateField('agency_digit', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('agency_digit')}
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="account-number">Conta</label>
                        <input
                           id="account-number"
                           type="text"
                           maxLength={30}
                           placeholder="56789"
                           value={form.account_number}
                           onChange={(e) =>
                              updateField('account_number', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('account_number')}
                     </Field>

                     <Field className="col-2">
                        <label htmlFor="account-digit">Dígito da conta</label>
                        <input
                           id="account-digit"
                           type="text"
                           maxLength={5}
                           placeholder="0"
                           value={form.account_digit}
                           onChange={(e) =>
                              updateField('account_digit', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('account_digit')}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            {/* ─── PIX ────────────────────────────────────────────────────── */}
            <Section>
               <SectionHeader>
                  <SectionTitle>PIX</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-8">
                        <label htmlFor="pix-key">Chave PIX</label>
                        <input
                           id="pix-key"
                           type="text"
                           maxLength={140}
                           placeholder="CNPJ, e-mail, telefone ou chave aleatória"
                           value={form.pix_key}
                           onChange={(e) =>
                              updateField('pix_key', e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                        {renderError('pix_key')}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="pix-key-type">Tipo da chave</label>
                        <select
                           id="pix-key-type"
                           value={form.pix_key_type}
                           onChange={(e) =>
                              updateField('pix_key_type', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.pix_key_type || undefined}
                        >
                           {PIX_KEY_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                        {renderError('pix_key_type')}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <ActionBar>
               <ActionBarLeft />
               <ActionBarRight>
                  <GhostButton
                     type="button"
                     onClick={handleCancel}
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                     {/* eslint-disable-next-line no-nested-ternary */}
                     {isSubmitting
                        ? 'Salvando…'
                        : isEdit
                          ? 'Salvar alterações'
                          : 'Criar conta'}
                  </PrimaryButton>
               </ActionBarRight>
            </ActionBar>
         </form>
      </Container>
   );
}
